import React from 'react';
import { Calendar as CalendarIcon, CheckCircle, Sparkles, Clock, MapPin, ArrowRight, X, Check } from 'lucide-react';
type JobSource = 'external' | 'platform' | 'ai';
interface Job {
  id: number;
  time: string;
  address: string;
  type: string;
  source: JobSource;
  externalSource?: 'google' | 'outlook';
  urgency?: 'Alta' | 'Media' | 'Bassa';
}
export function UpcomingJobsList() {
  // Mock data aligned with Planner model
  const jobs: Job[] = [{
    id: 1,
    time: '09:00 - 10:30',
    address: 'Via Cola di Rienzo 28',
    type: 'Riparazione Serratura',
    source: 'external',
    externalSource: 'google'
  }, {
    id: 2,
    time: '11:30 - 13:00',
    address: 'Via del Corso 150',
    type: 'Sostituzione Rubinetto',
    source: 'platform'
  }, {
    id: 3,
    time: '14:00 - 14:30',
    address: 'Via della Lungaretta 12',
    type: 'Sblocco Scarico',
    source: 'ai',
    urgency: 'Alta'
  }, {
    id: 4,
    time: '16:30 - 18:00',
    address: 'Via Marmorata 32',
    type: 'Manutenzione Condizionatore',
    source: 'ai',
    urgency: 'Media'
  }];
  const renderJobCard = (job: Job) => {
    const isAi = job.source === 'ai';
    const isExternal = job.source === 'external';
    const isPlatform = job.source === 'platform';
    return <div key={job.id} className={`flex flex-col gap-3 p-3 bg-white border rounded-xl shadow-sm transition-all ${isAi ? 'border-dashed border-teal-deep/40 bg-teal-50/10' : 'border-gray-200'}`} data-id="element-2604">
        <div className="flex items-start gap-3" data-id="element-2605">
          {/* Indicator Icon */}
          <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 border-2 shadow-sm ${isExternal ? 'bg-navy text-white border-white' : isPlatform ? 'bg-teal-deep text-white border-white' : 'bg-white text-teal-deep border-teal-deep'}`} data-id="element-2606">
            {isExternal ? <CalendarIcon size={14} data-id="element-2607" /> : isPlatform ? <CheckCircle size={14} data-id="element-2608" /> : <Sparkles size={14} data-id="element-2609" />}
          </div>

          <div className="flex-1 min-w-0" data-id="element-2610">
            {/* Header: Type & Source Badge */}
            <div className="flex justify-between items-start mb-1" data-id="element-2611">
              <p className="text-sm font-bold text-navy" data-id="element-2612">{job.type}</p>
              {isExternal && <div className="flex items-center gap-1 px-1.5 py-0.5 bg-gray-100 rounded text-[9px] font-bold text-gray-500 uppercase tracking-wider" data-id="element-2613">
                  {job.externalSource === 'google' ? <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/a/a5/Google_Calendar_icon_%282020%29.svg/500px-Google_Calendar_icon_%282020%29.svg.png" alt="Google" className="w-3 h-3 object-contain" data-id="element-2614" /> : <img src="https://img.icons8.com/color/600/outlook-calendar.png" alt="Outlook" className="w-3 h-3 object-contain" data-id="element-2615" />}
                  Esterno
                </div>}
              {isPlatform && <div className="px-1.5 py-0.5 bg-teal-50 text-teal-700 rounded text-[9px] font-bold uppercase tracking-wider border border-teal-100" data-id="element-2616">
                  Confermato
                </div>}
              {isAi && <div className="px-1.5 py-0.5 bg-teal-50 text-teal-700 rounded text-[9px] font-bold uppercase tracking-wider border border-teal-100 flex items-center gap-1" data-id="element-2617">
                  <Sparkles size={8} data-id="element-2618" />
                  Proposta
                </div>}
            </div>

            {/* Time & Duration */}
            <div className="flex items-center gap-1.5 text-xs text-gray-500 mb-1" data-id="element-2619">
              <Clock size={12} className="text-teal-deep" data-id="element-2620" />
              <span className="font-bold text-teal-deep" data-id="element-2621">{job.time}</span>
            </div>

            {/* Address */}
            <div className="flex items-center gap-1 text-[11px] text-gray-400" data-id="element-2622">
              <MapPin size={10} className="flex-shrink-0" data-id="element-2623" />
              <span className="truncate" data-id="element-2624">{job.address}</span>
            </div>
          </div>
        </div>

        {/* AI Actions */}
        {isAi && <div className="flex gap-2 mt-1 pl-11" data-id="element-2625">
            <button className="flex items-center justify-center gap-1.5 px-3 py-1.5 bg-teal-deep text-white rounded-lg text-xs font-medium hover:bg-teal-deep/90 transition-colors" data-id="element-2626">
              <Check size={12} data-id="element-2627" />
              Accetta
            </button>
            <button className="flex items-center justify-center gap-1.5 px-3 py-1.5 bg-white border border-gray-200 text-gray-500 rounded-lg text-xs font-medium hover:bg-gray-50 transition-colors" data-id="element-2628">
              <X size={12} data-id="element-2629" />
              Ignora
            </button>
          </div>}
      </div>;
  };
  return <div className="bg-white rounded-xl flex flex-col h-full border border-gray-200 overflow-hidden" data-id="element-2630">
      <div className="px-5 py-4 flex justify-between items-center bg-white border-b border-gray-100" data-id="element-2631">
        <h3 className="font-semibold text-navy text-sm" data-id="element-2632">Interventi di Oggi</h3>
        <span className="text-xs text-gray-400 font-medium" data-id="element-2633">
          {jobs.length} totali
        </span>
      </div>

      <div className="p-4 space-y-3 overflow-y-auto max-h-[600px] custom-scrollbar bg-gray-50/50" data-id="element-2634">
        {/* Group 1: Fixed/Confirmed */}
        <div className="space-y-3" data-id="element-2635">
          <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider pl-1" data-id="element-2636">
            In Programma
          </p>
          {jobs.filter(j => j.source !== 'ai').map(job => renderJobCard(job))}
        </div>

        {/* Group 2: AI Suggestions */}
        {jobs.some(j => j.source === 'ai') && <div className="space-y-3 pt-2" data-id="element-2637">
            <p className="text-[10px] font-bold text-teal-deep uppercase tracking-wider pl-1 flex items-center gap-1" data-id="element-2638">
              <Sparkles size={10} data-id="element-2639" />
              Suggeriti per te
            </p>
            {jobs.filter(j => j.source === 'ai').map(job => renderJobCard(job))}
          </div>}
      </div>
    </div>;
}